import { toBase } from "./fx.ts";
import { roundMoney } from "./money.ts";
import { sliceAmountForMonth } from "./charges.ts";
import { monthOf } from "./months.ts";
import {
  chargesFor,
  nextUnconfirmedOccurrenceOnOrAfter,
  occurrencesByMonth,
  type Occurrence,
} from "./occurrences.ts";
import type { BudgetFolder, Dataset, MonthId, Purchase, RecurringCost } from "./types.ts";
import type { MonthPostRow } from "./views.ts";

export type BudgetNode =
  | { kind: "folder"; folder: BudgetFolder; billsDue: number; children: BudgetNode[] }
  | { kind: "post"; row: MonthPostRow; children: BudgetNode[] }
  | { kind: "purchase"; purchase: Purchase; amount: NonNullable<ReturnType<typeof sliceAmountForMonth>> }
  | { kind: "bill"; cost: RecurringCost; occurrences: Occurrence[]; nextOccurrence: Occurrence | null };

export interface BudgetTreeView {
  roots: BudgetNode[];
  unfiled: BudgetNode[];
  unassigned: BudgetNode[];
}

/** Pure month-specific placement and scheduled bill prices. */
export function budgetTree(
  dataset: Dataset,
  monthId: MonthId,
  rows: readonly MonthPostRow[],
): BudgetTreeView {
  const slots = occurrencesByMonth(dataset, monthId).get(monthId) ?? [];
  const purchases = new Map(dataset.purchases.map(purchase => [purchase.id, purchase]));
  const slotsByBill = new Map<string, Occurrence[]>();
  for (const slot of slots) {
    const found = slotsByBill.get(slot.recurringId);
    if (found) found.push(slot);
    else slotsByBill.set(slot.recurringId, [slot]);
  }

  type OrderedNode = { order: number; node: BudgetNode };
  const byParent = new Map<string | null, OrderedNode[]>();
  const add = (parent: string | null, order: number, node: BudgetNode): void => {
    const found = byParent.get(parent);
    if (found) found.push({ order, node });
    else byParent.set(parent, [{ order, node }]);
  };

  for (const folder of dataset.folders) {
    add(folder.parentId, folder.order, { kind: "folder", folder, billsDue: 0, children: [] });
  }
  for (const row of rows) {
    add(row.post.folderId, row.post.order, { kind: "post", row, children: [] });
  }
  const postBills = new Map<string, BudgetNode[]>();
  for (const cost of dataset.recurring) {
    const node: BudgetNode = {
      kind: "bill",
      cost,
      occurrences: slotsByBill.get(cost.id) ?? [],
      nextOccurrence: nextUnconfirmedOccurrenceOnOrAfter(dataset, cost.id, monthId),
    };
    if (cost.parentPostId) {
      const children = postBills.get(cost.parentPostId) ?? [];
      children.push(node);
      postBills.set(cost.parentPostId, children);
    } else add(cost.folderId, cost.order, node);
  }
  const unassigned: BudgetNode[] = [];
  for (const purchase of dataset.purchases) {
    const amount = sliceAmountForMonth(purchase, monthId);
    if (!amount) continue;
    const node: BudgetNode = { kind: "purchase", purchase, amount };
    if (purchase.splits.length === 0) unassigned.push(node);
    else if (purchase.placement) add(purchase.placement.folderId, purchase.placement.order, node);
  }

  const digits = dataset.settings.digits;
  const inBase = (price: Purchase["total"]): number =>
    toBase(price, dataset.settings.baseCurrency, dataset.fxRates, digits);
  // An anchored bill's charges are Purchases, never confirmed occurrences, so
  // they are counted here by their own date; its occurrences are projections
  // only. Without this, a charged anchored bill would read as nothing due.
  const anchoredChargesDue = (cost: RecurringCost): number =>
    cost.anchoring !== "lastCharge" ? 0 : chargesFor(dataset.purchases, cost.id)
      .filter(charge => monthOf(charge.date) === monthId)
      .reduce((sum, charge) => roundMoney(sum + inBase(charge.total), digits), 0);
  const billDue = (cost: RecurringCost, occurrences: readonly Occurrence[]): number =>
    occurrences.reduce((sum, occurrence) => {
      const purchase = occurrence.confirmedBy ? purchases.get(occurrence.confirmedBy) : null;
      if (occurrence.confirmedBy && !purchase) {
        throw new Error(`Missing confirming purchase: ${occurrence.confirmedBy}`);
      }
      const price = purchase?.total ?? occurrence.amount;
      return roundMoney(sum + inBase(price), digits);
    }, anchoredChargesDue(cost));
  const dueInNode = (node: BudgetNode): number => {
    if (node.kind === "folder") return node.billsDue;
    if (node.kind === "bill") return billDue(node.cost, node.occurrences);
    if (node.kind === "post") return node.children.reduce((sum, child) => roundMoney(sum + dueInNode(child), digits), 0);
    return 0;
  };
  const childrenOf = (parent: string | null): BudgetNode[] =>
    (byParent.get(parent) ?? [])
      .sort((a, b) => a.order - b.order)
      .map(({ node }) => {
        if (node.kind === "folder") {
          node.children = childrenOf(node.folder.id);
          node.billsDue = node.children.reduce((sum, child) => roundMoney(sum + dueInNode(child), digits), 0);
        } else if (node.kind === "post") {
          node.children = (postBills.get(node.row.post.id) ?? []).sort((a, b) => a.kind === "bill" && b.kind === "bill" ? a.cost.order - b.cost.order : 0);
        }
        return node;
      });

  const rootItems = childrenOf(null);
  return {
    roots: rootItems.filter(node => node.kind === "folder"),
    unfiled: rootItems.filter(node => node.kind !== "folder"),
    unassigned,
  };
}
